import { createTheme } from "@material-ui/core/styles";

/**
 * Athletes Launch 2.0 — Design System (Hyper-Modern)
 *
 * Brand Gold:       #edd714  (primary)
 * Warm Gold:        #d4a017  (secondary accent)
 * Amber Gold:       #c49000  (tertiary accent)
 * Deep Gold:        #b8860b  (depth accent)
 * Surface Dark:     #060608 / #0d0d10 / #141418 / #1c1c22
 */

export const BRAND = {
    gold: '#edd714',
    goldDark: '#d4c212',
    goldWarm: '#d4a017',
    goldAmber: '#c49000',
    goldDeep: '#b8860b',
    surface0: '#060608',
    surface1: '#0d0d10',
    surface2: '#141418',
    surface3: '#1c1c22',
    surface4: '#24242c',
    white: '#f0f0f5',
    textPrimary: '#f0f0f5',
    textSecondary: 'rgba(240,240,245,0.65)',
    textTertiary: 'rgba(240,240,245,0.38)',
    border: 'rgba(255,255,255,0.06)',
    borderHover: 'rgba(237,215,20,0.2)',
    glass: 'rgba(12,12,16,0.72)',
    glassHeavy: 'rgba(8,8,12,0.88)',
    glassBorder: 'rgba(255,255,255,0.06)',
};

export default createTheme({
    palette: {
        type: 'dark',
        primary: {
            main: BRAND.gold,
        },
        secondary: {
            main: BRAND.goldWarm,
        },
        background: {
            default: BRAND.surface0,
            paper: BRAND.surface1,
        },
        text: {
            primary: BRAND.textPrimary,
            secondary: BRAND.textSecondary,
        },
    },
    typography: {
        fontFamily: "'Exo 2', 'Inter', 'Roboto', sans-serif",
        h1: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 700,
            letterSpacing: '-1px',
            lineHeight: 1.05,
        },
        h2: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 700,
            letterSpacing: '-0.5px',
            lineHeight: 1.1,
        },
        h3: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 700,
            letterSpacing: '-0.3px',
            lineHeight: 1.15,
        },
        h4: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 700,
            letterSpacing: '-0.2px',
        },
        h5: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.1px',
        },
        h6: {
            fontFamily: "'Orbitron', 'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: '13px',
            letterSpacing: '1.5px',
        },
        body1: {
            fontFamily: "'Exo 2', 'Inter', sans-serif",
            fontWeight: 400,
            lineHeight: 1.7,
            letterSpacing: '-0.01em',
        },
        body2: {
            fontFamily: "'Exo 2', 'Inter', sans-serif",
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: 1.6,
            letterSpacing: '-0.01em',
        },
        button: {
            fontFamily: "'Exo 2', 'Inter', sans-serif",
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: '0.01em',
        },
    },
    shape: {
        borderRadius: 18,
    },
    overrides: {
        MuiChip: {
            label: {
                paddingLeft: '16px',
                paddingRight: '16px',
            }
        },
        MuiCssBaseline: {
            '@global': {
                '*': {
                    'scrollbar-width': 'thin',
                },
                '*::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },
                '*::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    borderRadius: '3px',
                },
                '*::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(237,215,20,0.2)',
                },
                'body': {
                    minWidth: '940px',
                    width: 'auto !important',
                    height: '100vh',
                    backgroundColor: BRAND.surface0,
                }
            }
        },
        MuiButton: {
            root: {
                borderRadius: '14px',
                textTransform: 'none',
                fontWeight: 700,
                padding: '10px 24px',
                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            },
            containedPrimary: {
                color: '#0a0a0a',
                '&:hover': {
                    boxShadow: '0 8px 30px rgba(237,215,20,0.3)',
                    transform: 'translateY(-1px)',
                },
            },
        },
        MuiPaper: {
            root: {
                backgroundColor: BRAND.surface1,
                backgroundImage: 'none',
            },
            rounded: {
                borderRadius: '20px',
            },
            elevation1: {
                boxShadow: '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
            },
            elevation8: {
                boxShadow: '0 12px 48px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.25)',
            },
        },
        MuiDialog: {
            paper: {
                backgroundColor: 'rgba(12,12,16,0.92)',
                backdropFilter: 'blur(40px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.4)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '24px',
            },
        },
        MuiAppBar: {
            colorPrimary: {
                backgroundColor: 'rgba(6,6,8,0.85)',
                backdropFilter: 'blur(40px) saturate(1.4)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.4)',
            },
        },
        MuiIconButton: {
            root: {
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                },
            },
        },
        MuiTooltip: {
            tooltip: {
                backgroundColor: 'rgba(20,20,28,0.95)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
                padding: '8px 14px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            },
            arrow: {
                color: 'rgba(20,20,28,0.95)',
            },
        },
        MuiInputBase: {
            root: {
                transition: 'border-color 200ms ease, background-color 200ms ease',
            },
        },
    }
});
